import type { Ref } from "vue";
import { FetchResult } from "@apollo/client/core";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import type { CreateFixturesOrderMutation } from "@/generated/graphql";
import type { FixturesFormRef, FixturesItem, FixturesOrderInput } from "../types";

/**
 * 資材発注登録画面の操作処理に必要な依存をまとめた型。
 */
interface UseFixturesRegisterActionsParams {
  mode: Ref<number>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  order_note: Ref<string>;
  fixturesItems: Ref<(FixturesItem | null | undefined)[] | null | undefined>;
  fixturesOrderInput: Ref<FixturesOrderInput>;
  formRef: Ref<FixturesFormRef>;
  createFixturesOrder: (
    input: FixturesOrderInput
  ) => Promise<FetchResult<CreateFixturesOrderMutation>>;
}

/**
 * 資材発注登録画面の操作処理を扱う composable。
 */
export const useFixturesRegisterActions = ({
  mode,
  errdialog,
  errMessage,
  order_note,
  fixturesItems,
  fixturesOrderInput,
  formRef,
  createFixturesOrder,
}: UseFixturesRegisterActionsParams) => {
  /**
   * 登録対象のinput配列を作成する。
   */
  const buildFixturesOrderInput = (): FixturesOrderInput => {
    const input: NonNullable<FixturesOrderInput> = [];
    fixturesItems.value?.forEach((value) => {
      if (!value || value.fixtures_quantity === "-" || !value.fixtures_quantity) {
        return;
      }
      input.push({
        chain_code: localStorageGetChainCode(),
        customer_code: localStorageGetCustomerCode(),
        fixtures_id: value.fixtures_id ?? "",
        fixtures_quantity: Number(value.fixtures_quantity),
        order_note: order_note.value,
        shop_code: localStorageGetShopCode(),
        user_id: localStorageGetUserId(),
      });
    });
    return input;
  };

  /**
   * 「登録手続きへ(確認)」ボタン押下時に確認画面へ遷移する。
   */
  const onClickOpenConfirm = async (): Promise<void> => {
    const hasSelectedQuantity =
      fixturesItems.value?.some(
        (value) =>
          !!value &&
          value.fixtures_quantity !== undefined &&
          value.fixtures_quantity !== null &&
          value.fixtures_quantity !== "-"
      ) ?? false;

    if (!hasSelectedQuantity) {
      errdialog.value = true;
      errMessage.value = "少なくとも1つの資材は数量を選択してください。";
      return;
    }

    mode.value = 1;
  };

  /**
   * 「戻る」ボタン押下時に入力画面へ戻る。
   */
  const onClickConfirmCancel = async (): Promise<void> => {
    mode.value = 0;
  };

  /**
   * 「登録手続きへ(完了)」ボタン押下時に登録を実行する。
   */
  const onClickCompleted = async (): Promise<void> => {
    fixturesOrderInput.value = buildFixturesOrderInput();
    const result = await createFixturesOrder(fixturesOrderInput.value);
    console.log(result);
    mode.value = 2;
  };

  /**
   * 完了画面から入力画面へ戻し入力値を初期化する。
   */
  const onClickBackToStart = async (): Promise<void> => {
    if (fixturesItems.value?.length) {
      fixturesItems.value.forEach((value) => {
        if (value) {
          value.fixtures_quantity = "-";
          formRef.value?.setFieldValue(
            value.fixtures_name,
            value.fixtures_quantity ?? "-",
            false
          );
        }
      });
    }

    order_note.value = "";
    mode.value = 0;
  };

  return {
    onClickOpenConfirm,
    onClickConfirmCancel,
    onClickCompleted,
    onClickBackToStart,
  };
};
